import React from "react";

/**
 * Safety net around a rendered article body.
 *
 * Browser translate tools and some reader extensions swap text nodes out from
 * under React. When React later tries to move or remove a node that is no
 * longer where it left it, it throws a DOM NotFoundError and the whole page
 * goes white. This boundary catches that, remounts the body once with a fresh
 * key, and only shows a plain message if the second try also fails.
 */
interface Props {
  children: React.ReactNode;
}

interface State {
  failures: number;
  gaveUp: boolean;
}

export class DocBodyBoundary extends React.Component<Props, State> {
  state: State = { failures: 0, gaveUp: false };

  static getDerivedStateFromError() {
    return {};
  }

  componentDidCatch(error: unknown) {
    console.error("Article body failed to render:", error);
    this.setState((prev) => {
      const failures = prev.failures + 1;
      return { failures, gaveUp: failures > 1 };
    });
  }

  render() {
    if (this.state.gaveUp) {
      return (
        <div className="rounded-md border border-border bg-muted/40 p-5 text-sm">
          <p className="mb-3">
            This page had trouble showing the article. If your browser is
            translating the page, turn translation off and load it again.
          </p>
          <button
            type="button"
            className="underline font-medium"
            onClick={() => window.location.reload()}
          >
            Load the page again
          </button>
        </div>
      );
    }

    // The key changes after a failure, so the body mounts clean instead of
    // trying to patch a tree the browser already changed.
    return (
      <div key={`doc-body-${this.state.failures}`} translate="no">
        {this.props.children}
      </div>
    );
  }
}
